/**
 * Per-column display settings for a canvas VIEW_GRID: a configured column can render its values as
 * coloured status pills. Nothing changes for a column without a setting. Editable columns keep
 * their editor and write contract; enum options can use the same status colours.
 */
import React from "react";
import { formatRecordLabelTemplate } from "../recordLabelTemplate";
import type { GridCellParams, GridColDef } from "@mui/x-data-grid";
import {
  ColumnStatusColorsContext,
  GridInitialsAvatar,
  GridStatusPill,
} from "./GridCells";
import {
  sanitizeStatusColors,
  type StatusColor,
} from "../canvasBuilder/canvasStyleOverrides";

export interface ColumnDisplay {
  /** Presentation-only header text; field identity and behavior stay unchanged. */
  headerName?: string;
  statusColors?: Record<string, StatusColor>;
  recordLabelTemplate?: string;
  /** Render a person-name column as an initials avatar followed by the name. */
  avatar?: boolean;
  /** Column width in pixels (40 to 1000); any column, editable or not. */
  width?: number;
  /** Share remaining grid width proportionally; an explicit width takes precedence. */
  flex?: number;
}

const MIN_COLUMN_WIDTH = 40;
const MAX_COLUMN_WIDTH = 1000;

export type ColumnDisplayMap = Record<string, ColumnDisplay>;

/**
 * Read the display settings from an element config. A column's status colours are configured as
 * `columnStatusColors: { "<field>": { "<value>": "success" | "warning" | ... } }` (snake_case
 * `column_status_colors` is accepted too); unknown colour names are dropped. A person-name column is
 * configured as `avatarColumns: ["<field>"]` (or `avatar_columns`). A column width is configured as
 * `columnWidths: { "<field>": <pixels> }` (or `column_widths`).
 * `columnDisplay: { "<field>": { headerName: "<label>" } }` (or `column_display`)
 * changes only the header text.
 * `columnFlex: { "<field>": <positive weight> }` (or `column_flex`) shares spare width, unless
 * the same field has an explicit pixel width.
 */
export function readColumnDisplay(
  config: Record<string, unknown>,
): ColumnDisplayMap | undefined {
  const display: ColumnDisplayMap = {};
  const rawDisplay = config.columnDisplay ?? config.column_display;
  if (
    rawDisplay &&
    typeof rawDisplay === "object" &&
    !Array.isArray(rawDisplay)
  ) {
    for (const [field, setting] of Object.entries(rawDisplay)) {
      if (!setting || typeof setting !== "object" || Array.isArray(setting))
        continue;
      const headerName = (setting as Record<string, unknown>).headerName;
      if (typeof headerName === "string" && headerName.trim()) {
        display[field] = { headerName: headerName.trim() };
      }
    }
  }
  const rawStatus = config.columnStatusColors ?? config.column_status_colors;
  if (rawStatus && typeof rawStatus === "object" && !Array.isArray(rawStatus)) {
    for (const [field, colours] of Object.entries(
      rawStatus as Record<string, unknown>,
    )) {
      const statusColors = sanitizeStatusColors(colours);
      if (statusColors) display[field] = { ...display[field], statusColors };
    }
  }
  const rawAvatars = config.avatarColumns ?? config.avatar_columns;
  if (Array.isArray(rawAvatars)) {
    for (const field of rawAvatars) {
      if (typeof field === "string" && field) {
        display[field] = { ...display[field], avatar: true };
      }
    }
  }
  const rawWidths = config.columnWidths ?? config.column_widths;
  if (rawWidths && typeof rawWidths === "object" && !Array.isArray(rawWidths)) {
    for (const [field, value] of Object.entries(
      rawWidths as Record<string, unknown>,
    )) {
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        const width = Math.min(
          MAX_COLUMN_WIDTH,
          Math.max(MIN_COLUMN_WIDTH, Math.round(value)),
        );
        display[field] = { ...display[field], width };
      }
    }
  }
  const rawFlex = config.columnFlex ?? config.column_flex;
  if (rawFlex && typeof rawFlex === "object" && !Array.isArray(rawFlex)) {
    for (const [field, value] of Object.entries(
      rawFlex as Record<string, unknown>,
    )) {
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        display[field] = { ...display[field], flex: Math.min(10, value) };
      }
    }
  }
  const templates =
    config.columnRecordLabelTemplates ?? config.column_record_label_templates;
  if (templates && typeof templates === "object" && !Array.isArray(templates)) {
    for (const [field, template] of Object.entries(templates)) {
      if (typeof template === "string" && template.trim()) {
        display[field] = { ...display[field], recordLabelTemplate: template };
      }
    }
  }
  return Object.keys(display).length > 0 ? display : undefined;
}

/** Apply presentation while retaining each column's editability and existing editor. */
export function applyColumnDisplay(
  columns: GridColDef[],
  display: ColumnDisplayMap | undefined,
): GridColDef[] {
  if (!display) return columns;
  return columns.map((sized) => {
    const setting = display[sized.field];
    // Explicit widths take precedence over flexible allocation.
    const labelled = setting?.headerName
      ? { ...sized, headerName: setting.headerName }
      : sized;
    const column = setting?.width
      ? { ...labelled, width: setting.width, flex: 0 }
      : setting?.flex
        ? {
            ...labelled,
            flex: setting.flex,
            minWidth: sized.minWidth ?? MIN_COLUMN_WIDTH,
          }
        : labelled;
    if (!setting) return column;
    const { statusColors, avatar, recordLabelTemplate } = setting;
    if (recordLabelTemplate) {
      return {
        ...column,
        renderCell: (params: GridCellParams) => (
          <span>
            {formatRecordLabelTemplate(recordLabelTemplate, params.row)}
          </span>
        ),
      };
    }
    if (statusColors) {
      return {
        ...column,
        renderCell: (params: GridCellParams) => (
          <GridStatusPill
            value={String(params.value ?? "")}
            statusColors={statusColors}
          />
        ),
        ...(column.editable && column.renderEditCell
          ? {
              renderEditCell: (params) => (
                <ColumnStatusColorsContext.Provider value={statusColors}>
                  {column.renderEditCell!(params)}
                </ColumnStatusColorsContext.Provider>
              ),
            }
          : {}),
      };
    }
    if (avatar && !column.editable) {
      return {
        ...column,
        renderCell: (params: GridCellParams) => (
          <GridInitialsAvatar name={String(params.value ?? "")} />
        ),
      };
    }
    return column;
  });
}
